import { isJsonObject } from "@/types/json"
import {
  COMPARISON_PLOT_SCALES,
  COMPARISON_PLOT_TYPES,
  type ComparisonPlotConfig,
  type ComparisonPlotScale,
  type ComparisonPlotType,
  type ScatterPlotConfig,
} from "@/utils/comparisonPlot"
import type {
  MetricLayoutConfig,
  MetricLayoutItem,
  MetricLayoutSection,
} from "@/utils/metricLayout"

import { COMPARE_BY_OPTIONS, type CompareByOption } from "../../compare-by"

export type ReportViewMode = "rows" | "grouped"

export interface CustomDimensionGroup {
  id: string
  name: string
  simulationIds: string[]
}

export interface CustomDimension {
  id: string
  name: string
  groups: CustomDimensionGroup[]
  hideUnassigned: boolean
}

export interface CustomDimensionsConfig {
  dimensions: CustomDimension[]
}

/** Which of the workspace's two panes the report shows. At least one pane is
 *  always visible — a config asking for neither falls back to both. */
export interface ReportPaneVisibility {
  metrics: boolean
  table: boolean
}

export const DEFAULT_REPORT_PANE_VISIBILITY: ReportPaneVisibility = {
  metrics: true,
  table: true,
}

export function resolveReportPaneVisibility(
  value: unknown,
): ReportPaneVisibility {
  if (!isJsonObject(value)) {
    return DEFAULT_REPORT_PANE_VISIBILITY
  }
  const metrics = value.metrics !== false
  const table = value.table !== false
  return metrics || table ? { metrics, table } : DEFAULT_REPORT_PANE_VISIBILITY
}

export interface SavedReportViewConfig {
  compareBy?: CompareByOption
  metadataKey?: string | null
  customDimensionId?: string | null
  viewMode?: ReportViewMode
  secondaryCompareBy?: CompareByOption
  secondaryMetadataKey?: string | null
  secondaryCustomDimensionId?: string | null
  compareByColorOverrides?: Record<string, string>
  monitoringFilters?: Record<string, unknown>
  isMonitoring?: boolean
  customDimensions?: CustomDimensionsConfig
  metricLayout?: MetricLayoutConfig
  paneVisibility?: ReportPaneVisibility
  /** Set when the report was generated from a Human Review project. */
  sourceHumanReviewProjectId?: string
}

const VALID_COMPARE_BY_OPTIONS: ReadonlySet<string> = new Set(
  COMPARE_BY_OPTIONS,
)

function isCompareByOption(value: unknown): value is CompareByOption {
  return (
    typeof value === "string" &&
    VALID_COMPARE_BY_OPTIONS.has(value as CompareByOption)
  )
}

function isViewMode(value: unknown): value is ReportViewMode {
  return value === "rows" || value === "grouped"
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    isJsonObject(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  )
}

function parseCustomDimensionGroup(
  value: unknown,
): CustomDimensionGroup | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (typeof value.id !== "string" || value.id.length === 0) {
    return null
  }
  if (typeof value.name !== "string") {
    return null
  }
  if (!Array.isArray(value.simulationIds)) {
    return null
  }
  const simulationIds = value.simulationIds.filter(
    (entry): entry is string => typeof entry === "string" && entry.length > 0,
  )
  return { id: value.id, name: value.name, simulationIds }
}

function parseCustomDimension(value: unknown): CustomDimension | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (typeof value.id !== "string" || value.id.length === 0) {
    return null
  }
  if (typeof value.name !== "string") {
    return null
  }
  if (!Array.isArray(value.groups)) {
    return null
  }
  const groups: CustomDimensionGroup[] = []
  for (const entry of value.groups) {
    const parsed = parseCustomDimensionGroup(entry)
    if (parsed) {
      groups.push(parsed)
    }
  }
  const hideUnassigned =
    typeof value.hideUnassigned === "boolean" ? value.hideUnassigned : false
  return { id: value.id, name: value.name, groups, hideUnassigned }
}

const VALID_PLOT_TYPES: ReadonlySet<string> = new Set(COMPARISON_PLOT_TYPES)

function parseOptionalMetricId(value: unknown): string | null | undefined {
  if (typeof value === "string") {
    return value
  }
  return value === null ? null : undefined
}

function parseScale(value: unknown): ComparisonPlotScale | undefined {
  return typeof value === "string" &&
    (COMPARISON_PLOT_SCALES as readonly string[]).includes(value)
    ? (value as ComparisonPlotScale)
    : undefined
}

// Finite positive numbers only — shared by pow exponents and the outlier k.
function parsePositiveNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined
}

function parseScatterPlot(
  value: Record<string, unknown>,
  id: string,
  title: string | undefined,
): ScatterPlotConfig {
  const plot: ScatterPlotConfig = { id, type: "scatter" }
  if (title !== undefined) {
    plot.title = title
  }
  const x = parseOptionalMetricId(value.xAxisMetricId)
  if (x !== undefined) {
    plot.xAxisMetricId = x
  }
  const y = parseOptionalMetricId(value.yAxisMetricId)
  if (y !== undefined) {
    plot.yAxisMetricId = y
  }
  const z = parseOptionalMetricId(value.zAxisMetricId)
  if (z !== undefined) {
    plot.zAxisMetricId = z
  }
  if (typeof value.showTrendLine === "boolean") {
    plot.showTrendLine = value.showTrendLine
  }
  if (typeof value.showLegend === "boolean") {
    plot.showLegend = value.showLegend
  }
  if (typeof value.excludeOutliers === "boolean") {
    plot.excludeOutliers = value.excludeOutliers
  }
  const outlierMultiplier = parsePositiveNumber(value.outlierIqrMultiplier)
  if (outlierMultiplier !== undefined) {
    plot.outlierIqrMultiplier = outlierMultiplier
  }
  const xScale = parseScale(value.xAxisScale)
  if (xScale) {
    plot.xAxisScale = xScale
  }
  const yScale = parseScale(value.yAxisScale)
  if (yScale) {
    plot.yAxisScale = yScale
  }
  const zScale = parseScale(value.zAxisScale)
  if (zScale) {
    plot.zAxisScale = zScale
  }
  const xExponent = parsePositiveNumber(value.xAxisScaleExponent)
  if (xExponent !== undefined) {
    plot.xAxisScaleExponent = xExponent
  }
  const yExponent = parsePositiveNumber(value.yAxisScaleExponent)
  if (yExponent !== undefined) {
    plot.yAxisScaleExponent = yExponent
  }
  const zExponent = parsePositiveNumber(value.zAxisScaleExponent)
  if (zExponent !== undefined) {
    plot.zAxisScaleExponent = zExponent
  }
  return plot
}

// Per-type dispatch on the persisted discriminant.
function parseComparisonPlot(value: unknown): ComparisonPlotConfig | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (typeof value.id !== "string" || value.id.length === 0) {
    return null
  }
  if (typeof value.type !== "string" || !VALID_PLOT_TYPES.has(value.type)) {
    return null
  }
  const title = typeof value.title === "string" ? value.title : undefined
  const type = value.type as ComparisonPlotType
  if (type === "scatter") {
    return parseScatterPlot(value, value.id, title)
  }
  // A plot type added to COMPARISON_PLOT_TYPES without a parse branch above
  // fails to compile here instead of silently dropping saved plots.
  return type satisfies never
}

function parseMetricLayoutItem(value: unknown): MetricLayoutItem | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (value.kind === "plot") {
    const plot = parseComparisonPlot(value.plot)
    return plot ? { kind: "plot", plot } : null
  }
  if (value.kind === "metric") {
    return typeof value.metricId === "string" && value.metricId.length > 0
      ? { kind: "metric", metricId: value.metricId }
      : null
  }
  return null
}

function parseMetricLayoutSection(value: unknown): MetricLayoutSection | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (typeof value.id !== "string" || value.id.length === 0) {
    return null
  }
  if (typeof value.title !== "string" || value.title.length === 0) {
    return null
  }

  // Current shape: an ordered (metric | plot) items union.
  if (Array.isArray(value.items)) {
    const items: MetricLayoutItem[] = []
    for (const entry of value.items) {
      const parsed = parseMetricLayoutItem(entry)
      if (parsed) {
        items.push(parsed)
      }
    }
    return { id: value.id, title: value.title, items }
  }

  // Legacy shape: bare metricIds map to metric items.
  if (Array.isArray(value.metricIds)) {
    const items: MetricLayoutItem[] = value.metricIds
      .filter(
        (entry): entry is string =>
          typeof entry === "string" && entry.length > 0,
      )
      .map((metricId) => ({ kind: "metric", metricId }))
    return { id: value.id, title: value.title, items }
  }

  return null
}

function parseMetricLayout(value: unknown): MetricLayoutConfig | undefined {
  if (!isJsonObject(value) || !Array.isArray(value.sections)) {
    return undefined
  }
  const sections: MetricLayoutSection[] = []
  for (const entry of value.sections) {
    const parsed = parseMetricLayoutSection(entry)
    if (parsed) {
      sections.push(parsed)
    }
  }
  return { sections }
}

function parseCustomDimensionsConfig(
  value: unknown,
): CustomDimensionsConfig | null {
  if (!isJsonObject(value)) {
    return null
  }
  if (!Array.isArray(value.dimensions)) {
    return null
  }
  const dimensions: CustomDimension[] = []
  for (const entry of value.dimensions) {
    const parsed = parseCustomDimension(entry)
    if (parsed) {
      dimensions.push(parsed)
    }
  }
  return { dimensions }
}

export function parseSavedReportViewConfig(
  value: unknown,
): SavedReportViewConfig | null {
  if (!isJsonObject(value)) {
    return null
  }

  const compareBy = isCompareByOption(value.compareBy)
    ? value.compareBy
    : undefined
  const metadataKey =
    typeof value.metadataKey === "string"
      ? value.metadataKey
      : value.metadataKey === null
        ? null
        : undefined
  const viewMode = isViewMode(value.viewMode) ? value.viewMode : undefined
  const secondaryCompareBy = isCompareByOption(value.secondaryCompareBy)
    ? value.secondaryCompareBy
    : undefined
  const secondaryMetadataKey =
    typeof value.secondaryMetadataKey === "string"
      ? value.secondaryMetadataKey
      : value.secondaryMetadataKey === null
        ? null
        : undefined
  const compareByColorOverrides = isStringRecord(value.compareByColorOverrides)
    ? value.compareByColorOverrides
    : undefined
  const monitoringFilters = isJsonObject(value.monitoringFilters)
    ? value.monitoringFilters
    : undefined
  const isMonitoring =
    typeof value.isMonitoring === "boolean" ? value.isMonitoring : undefined
  const customDimensions =
    parseCustomDimensionsConfig(value.customDimensions) ?? undefined
  const metricLayout = parseMetricLayout(value.metricLayout)
  const paneVisibility = isJsonObject(value.paneVisibility)
    ? resolveReportPaneVisibility(value.paneVisibility)
    : undefined
  const customDimensionId =
    typeof value.customDimensionId === "string"
      ? value.customDimensionId
      : value.customDimensionId === null
        ? null
        : undefined
  const secondaryCustomDimensionId =
    typeof value.secondaryCustomDimensionId === "string"
      ? value.secondaryCustomDimensionId
      : value.secondaryCustomDimensionId === null
        ? null
        : undefined
  // The persisted value is snake_case; map it onto the camelCase config field.
  const sourceHumanReviewProjectId =
    typeof value.source_human_review_project_id === "string"
      ? value.source_human_review_project_id
      : undefined

  return {
    compareBy,
    metadataKey,
    customDimensionId,
    viewMode,
    secondaryCompareBy,
    secondaryMetadataKey,
    secondaryCustomDimensionId,
    compareByColorOverrides,
    monitoringFilters,
    isMonitoring,
    customDimensions,
    metricLayout,
    paneVisibility,
    sourceHumanReviewProjectId,
  }
}
